import React, { useState, useMemo } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Checkbox, Button, Chip, Typography, TextField
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

const SERVICE_ROLE_META = {
  assembly: { label: 'Assembly', group: 'Assemblies / Packages', color: '#6a1b9a', bg: '#f3e5f5', rank: 0 },
  base_service: { label: 'Service', group: 'Base Services', color: '#1565c0', bg: '#e3f2fd', rank: 1 },
  child_service: { label: 'Child', group: 'Child Services', color: '#ef6c00', bg: '#fff3e0', rank: 2 }
}

const serviceRoleMeta = (service) => SERVICE_ROLE_META[service?.serviceRole] || SERVICE_ROLE_META.base_service

const ServiceAssigner = ({ items, aggregatedServices, services, onAssign, onRemove }) => {
  const [selected, setSelected] = useState([])
  const [assignServiceId, setAssignServiceId] = useState(null)
  const [collapsed, setCollapsed] = useState(false)

  // Group services by category for autocomplete
  const serviceOptions = useMemo(() => {
    return (services || []).map(s => ({
      serviceId: s.serviceId,
      name: s.name,
      category: s.category,
      serviceRole: s.serviceRole
    })).sort((a, b) => {
      const roleDiff = serviceRoleMeta(a).rank - serviceRoleMeta(b).rank
      if (roleDiff !== 0) return roleDiff
      const categoryDiff = String(a.category || '').localeCompare(String(b.category || ''))
      if (categoryDiff !== 0) return categoryDiff
      return String(a.name || '').localeCompare(String(b.name || ''))
    })
  }, [services])

  // Build lookup: itemId → [{ serviceId, serviceName, serviceCategory }]
  const itemServiceMap = useMemo(() => {
    const map = {}
    for (const svc of (aggregatedServices || [])) {
      for (const item of (svc.items || [])) {
        if (!map[item._id]) map[item._id] = []
        map[item._id].push({
          serviceId: svc.serviceId,
          serviceName: svc.serviceName,
          serviceCategory: svc.serviceCategory
        })
      }
    }
    return map
  }, [aggregatedServices])

  const activeItems = (items || []).filter(it => it.mappingStatus !== 'ignored')
  const taggedCount = activeItems.filter(it => (itemServiceMap[it._id] || []).length > 0).length

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? activeItems.map(i => i._id) : [])
  }

  const handleAssign = () => {
    if (!assignServiceId || selected.length === 0) return
    const svc = serviceOptions.find(s => s.serviceId === assignServiceId)
    if (!svc) return
    onAssign(selected, svc.serviceId, svc.name, svc.category)
    setSelected([])
    setAssignServiceId(null)
  }

  if (collapsed) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography variant='subtitle2' style={{ fontWeight: 700 }}>Bid Response Items</Typography>
            <Chip size='small' label={`${taggedCount} of ${activeItems.length} tagged`}
              style={{
                backgroundColor: taggedCount === activeItems.length ? '#e8f5e9' : '#fff3e0',
                color: taggedCount === activeItems.length ? '#2e7d32' : '#e65100'
              }} />
          </div>
          <Button size='small' onClick={() => setCollapsed(false)}>Expand ▼</Button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography variant='subtitle2' style={{ fontWeight: 700 }}>Bid Response Items</Typography>
          <Typography variant='caption' color='textSecondary'>Tag each item with the service(s) needed</Typography>
          <Chip size='small' label={`${taggedCount} of ${activeItems.length} tagged`}
            style={{
              backgroundColor: taggedCount === activeItems.length ? '#e8f5e9' : '#fff3e0',
              color: taggedCount === activeItems.length ? '#2e7d32' : '#e65100'
            }} />
        </div>
        <Button size='small' onClick={() => setCollapsed(true)}>Collapse ▲</Button>
      </div>

      {/* Bulk assign bar */}
      {selected.length > 0 && (
        <Paper style={{ padding: '8px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, backgroundColor: '#e3f2fd', flexWrap: 'wrap' }}>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            {selected.length} item{selected.length !== 1 ? 's' : ''} selected
          </Typography>
          <Autocomplete
            options={serviceOptions}
            groupBy={opt => serviceRoleMeta(opt).group}
            getOptionLabel={opt => opt.name || ''}
            value={serviceOptions.find(s => s.serviceId === assignServiceId) || null}
            onChange={(e, val) => setAssignServiceId(val ? val.serviceId : null)}
            size='small'
            style={{ minWidth: 280 }}
            renderOption={(opt) => {
              const role = serviceRoleMeta(opt)
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Typography variant='body2' style={{ fontSize: 12, lineHeight: 1.25 }}>{opt.name}</Typography>
                    <Typography variant='caption' style={{ color: '#777', fontSize: 10 }}>{opt.category || 'Other'} · {opt.serviceId}</Typography>
                  </div>
                  <Chip size='small' label={role.label} style={{ height: 18, fontSize: 9, backgroundColor: role.bg, color: role.color }} />
                </div>
              )
            }}
            renderInput={(params) => <TextField {...params} label='Assign service' variant='outlined' size='small' />}
          />
          <Button size='small' variant='contained' color='primary' onClick={handleAssign}
            disabled={!assignServiceId}>
            Assign
          </Button>
          <Button size='small' onClick={() => setSelected([])}>Clear</Button>
        </Paper>
      )}

      <TableContainer component={Paper} style={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < activeItems.length}
                  checked={activeItems.length > 0 && selected.length === activeItems.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align='right'>Qty</TableCell>
              <TableCell>Service(s)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeItems.map(item => {
              const isSelected = selected.includes(item._id)
              const itemServices = itemServiceMap[item._id] || []
              return (
                <TableRow key={item._id} hover selected={isSelected}>
                  <TableCell padding='checkbox'>
                    <Checkbox checked={isSelected}
                      onChange={() => setSelected(prev =>
                        prev.includes(item._id) ? prev.filter(x => x !== item._id) : [...prev, item._id]
                      )} />
                  </TableCell>
                  <TableCell>
                    <Typography variant='caption' style={{ fontFamily: 'monospace' }}>
                      {item.itemNo || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.descriptionNormalized || item.descriptionRaw || '—'}</TableCell>
                  <TableCell>{item.unitNormalized || item.unitRaw || '—'}</TableCell>
                  <TableCell align='right'>{item.quantity != null ? item.quantity : '—'}</TableCell>
                  <TableCell>
                    {itemServices.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {itemServices.map(a => (
                          <Chip key={a.serviceId} label={a.serviceName} size='small'
                            onDelete={() => onRemove([item._id], a.serviceId)}
                            style={{ fontSize: 11 }} />
                        ))}
                      </div>
                    ) : (
                      <Typography variant='caption' color='textSecondary' style={{ fontStyle: 'italic' }}>
                        Select & assign above
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default ServiceAssigner
